/**
 * 管理员服务
 * 封装所有管理员相关的 API 调用和业务逻辑
 */
import { apiClient } from "../../../config/netConfig";
import { message } from "antd";
import { AdminRecord } from "../config/panelTableConfig";

// 接口定义
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: number;
}

export interface CreateAdminParams {
  admin_account?: string;
  account?: string;
  password?: string;
  admin_password?: string;
  description?: string;
  account_status?: number;
}

export interface UpdateAdminParams {
  admin_account?: string;
  description?: string;
  account_status?: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

// 统一的 API 请求处理函数
const request = async <T>(url: string, options?: any): Promise<ApiResponse<T>> => {
  try {
    const response = await apiClient.request({ url, ...options });
    const responseData = response.data;

    if (response.status === 200) {
      // 如果响应已经包含标准格式
      if ("success" in responseData && "data" in responseData && "message" in responseData) {
        return responseData;
      }
      // 否则包装成标准格式
      return { success: true, data: responseData, message: "操作成功" };
    } else {
      // 处理 API 错误
      const errorMessage = responseData.message || `请求失败: ${response.status}`;
      message.error(errorMessage);
      return { success: false, data: responseData, message: errorMessage };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "请求失败";
    message.error(errorMessage);
    throw error; // 重新抛出，让调用方处理
  }
};

export const AdminService = {
  /**
   * 获取管理员列表
   */
  getAdminList: async (params: PaginationParams = {}): Promise<ApiResponse<{ items: AdminRecord[]; total: number }>> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });
    return request(`/admin/list?${searchParams.toString()}`);
  },

  /**
   * 获取管理员详情
   */
  getAdminDetail: async (id: string): Promise<ApiResponse<AdminRecord>> => {
    return request(`/admin/${id}`);
  },

  /**
   * 创建管理员
   */
  createAdmin: async (params: CreateAdminParams): Promise<ApiResponse<AdminRecord>> => {
    // 将admin_password转换为password字段名，以匹配后端接口
    const requestParams = {
      account: params.account || params.admin_account,
      admin_account: params.admin_account || params.account,
      password: params.password ?? params.admin_password,
      description: params.description || "",
      is_super_admin: false // 默认不是超级管理员
    };
    return request("/admin/add", {
      method: "POST",
      data: requestParams,
    });
  },

  /**
   * 更新管理员
   */
  updateAdmin: async (id: string, params: UpdateAdminParams): Promise<ApiResponse<AdminRecord>> => {
    return request(`/admin/${id}`, {
      method: "PUT",
      data: params,
    });
  },

  /**
   * 更新管理员状态
   */
  updateAdminStatus: async (id: string, status: number): Promise<ApiResponse<any>> => {
    return request(`/admin/${id}/status`, {
      method: "PUT",
      data: { status: status },
    });
  },

  /**
   * 重置密码
   */
  resetPassword: async (id: string): Promise<ApiResponse<any>> => {
    return request(`/admin/${id}/reset-pwd`, {
      method: "POST",
      data: {},
    });
  },

  /**
   * 删除管理员
   */
  deleteAdmin: async (id: string): Promise<ApiResponse<any>> => {
    return request(`/admin/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * 批量删除管理员
   */
  batchDeleteAdmins: async (ids: string[]): Promise<ApiResponse<any>> => {
    return request("/admin/batch-delete", {
      method: "POST",
      data: { ids },
    });
  },
};